import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  StyleSheet, Text, View, ScrollView, Image,
  TouchableOpacity, Platform, Animated, ImageBackground,
  useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { LinearGradient } from 'expo-linear-gradient';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import HarmoniaLogo from '../components/HarmoniaLogo';
import { registerServiceWorker } from '../lib/push';
import { getSavedAccounts } from '../lib/savedAccounts';
import { saveLanguage } from '../services/storage';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// ─── Palette ──────────────────────────────────────────────────────────────────
const P = {
  purple:     '#6B21A8',
  purpleL:    '#7C3AED',
  purpleSoft: '#F3EEFF',
  gold:       '#F59E0B',
  goldSoft:   '#FFF8E8',
  blue:       '#2563EB',
  blueSoft:   '#EFF6FF',
  green:      '#059669',
  greenSoft:  '#ECFDF5',
  bg:         '#FAFAFA',
  white:      '#FFFFFF',
  text:       '#111827',
  textSub:    '#6B7280',
  textLight:  '#9CA3AF',
  border:     '#E5E7EB',
  shadow:     'rgba(107,33,168,0.10)',
};

// ─── Langues disponibles pour le sélecteur ─────────────────────────────────────
const LANGUAGES = [
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' },
  { code: 'pt', flag: '🇵🇹', label: 'PT' },
];

const DISCIPLINE_META = [
  {
    id: 'strategie',
    icon: 'extension-puzzle-outline' as const,
    accent: '#A78BFA',
    gradient: ['#1A0840', '#2D1469'] as [string, string],
    itemCount: 2,
  },
  {
    id: 'connaissance',
    icon: 'bulb-outline' as const,
    accent: '#38BDF8',
    gradient: ['#001830', '#002D50'] as [string, string],
    itemCount: 1,
  },
  {
    id: 'arts',
    icon: 'color-palette-outline' as const,
    accent: '#F472B6',
    gradient: ['#2D0A3A', '#4A1259'] as [string, string],
    itemCount: 3,
  },
  {
    id: 'performance',
    icon: 'mic-outline' as const,
    accent: '#FBBF24',
    gradient: ['#1A0800', '#351200'] as [string, string],
    itemCount: 3,
  },
  {
    id: 'musique',
    icon: 'musical-notes-outline' as const,
    accent: '#2DD4BF',
    gradient: ['#001A1A', '#003030'] as [string, string],
    itemCount: 3,
  },
  {
    id: 'artisanat',
    icon: 'construct-outline' as const,
    accent: '#F59E0B',
    gradient: ['#1A0C00', '#2E1800'] as [string, string],
    itemCount: 5,
  },
];

type Discipline = {
  id: string;
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  tagline: string;
  desc: string;
  accent: string;
  gradient: [string, string];
  items: { name: string; sub: string }[];
};

function getDisciplines(t: (key: string) => string): Discipline[] {
  return DISCIPLINE_META.map((meta) => {
    const base = `landing.disciplines.${meta.id}`;
    const items = Array.from({ length: meta.itemCount }, (_, i) => ({
      name: t(`${base}.item${i + 1}Name`),
      sub: t(`${base}.item${i + 1}Sub`),
    }));
    return {
      id: meta.id,
      icon: meta.icon,
      accent: meta.accent,
      gradient: meta.gradient,
      label: t(`${base}.label`),
      tagline: t(`${base}.tagline`),
      desc: t(`${base}.desc`),
      items,
    };
  });
}

const Chip = ({ label, color, bg }: { label: string; color: string; bg: string }) => (
  <View style={[styles.chip, { backgroundColor: bg }]}>
    <Text style={[styles.chipTxt, { color }]}>{label}</Text>
  </View>
);

const ValueCard = ({ icon, title, desc, bg, color }: {
  icon: string; title: string; desc: string; bg: string; color: string;
}) => {
  const scale = useRef(new Animated.Value(1)).current;
  const onPressIn  = () => Animated.spring(scale, { toValue: 0.97, useNativeDriver: true, speed: 30 }).start();
  const onPressOut = () => Animated.spring(scale, { toValue: 1,    useNativeDriver: true, speed: 30 }).start();
  return (
    <Animated.View style={[styles.valueCard, { transform: [{ scale }] }]}>
      <TouchableOpacity activeOpacity={1} onPressIn={onPressIn} onPressOut={onPressOut} style={{ flex: 1 }}>
        <View style={[styles.valueIconBox, { backgroundColor: bg }]}>
          <Text style={{ fontSize: 30 }}>{icon}</Text>
        </View>
        <View style={[styles.valueAccent, { backgroundColor: color }]} />
        <Text style={styles.valueTitle}>{title}</Text>
        <Text style={styles.valueDesc}>{desc}</Text>
      </TouchableOpacity>
    </Animated.View>
  );
};

const DisciplineCard = ({
  discipline,
  onPress,
  isWebLarge,
  ctaLabel,
}: {
  discipline: Discipline;
  onPress: () => void;
  isWebLarge: boolean;
  ctaLabel: string;
}) => {
  const scale = useRef(new Animated.Value(1)).current;
  const onPressIn  = () => Animated.spring(scale, { toValue: 0.97, useNativeDriver: true, speed: 40 }).start();
  const onPressOut = () => Animated.spring(scale, { toValue: 1,    useNativeDriver: true, speed: 30 }).start();

  return (
    <TouchableOpacity onPress={onPress} onPressIn={onPressIn} onPressOut={onPressOut} activeOpacity={1}
      style={isWebLarge ? styles.discCardWrapWeb : styles.discCardWrapMobile}>
      <Animated.View style={[styles.discCard, { transform: [{ scale }] }]}>
        <LinearGradient colors={discipline.gradient} style={styles.discCardInner}
          start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}>

          <View style={[styles.discOrb, { backgroundColor: discipline.accent }]} />

          <View style={styles.discHeader}>
            <View style={[styles.discIconWrap, { borderColor: discipline.accent + '60' }]}>
              <Ionicons name={discipline.icon} size={22} color={discipline.accent} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.discLabel}>{discipline.label}</Text>
              <Text style={[styles.discTagline, { color: discipline.accent }]}>{discipline.tagline}</Text>
            </View>
          </View>

          <Text style={styles.discDesc}>{discipline.desc}</Text>

          <View style={[styles.discLine, { backgroundColor: discipline.accent + '30' }]} />

          <View style={styles.discItemsCol}>
            {discipline.items.map((item, i) => (
              <View key={i} style={styles.discItemRow}>
                <View style={[styles.discItemDot, { backgroundColor: discipline.accent }]} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.discItemName}>{item.name}</Text>
                  <Text style={[styles.discItemSub, { color: discipline.accent + 'CC' }]}>{item.sub}</Text>
                </View>
                <Ionicons name="chevron-forward" size={12} color={discipline.accent + '70'} />
              </View>
            ))}
          </View>

          <View style={[styles.discCta, { borderColor: discipline.accent + '40' }]}>
            <Ionicons name="trophy-outline" size={12} color={discipline.accent} />
            <Text style={[styles.discCtaTxt, { color: discipline.accent }]}>{ctaLabel}</Text>
          </View>

        </LinearGradient>
      </Animated.View>
    </TouchableOpacity>
  );
};

const HeroStat = ({ value, label }: { value: string; label: string }) => (
  <View style={styles.heroStat}>
    <Text style={styles.heroStatValue}>{value}</Text>
    <Text style={styles.heroStatLabel}>{label}</Text>
  </View>
);

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const router     = useRouter();
  const { width }  = useWindowDimensions();
  const isWebLarge = width > 800;

  const DISCIPLINES = getDisciplines(t);

  const [sessionChecked, setSessionChecked] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const currentLanguage = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleChangeLanguage = useCallback(async (code: string) => {
    await i18n.changeLanguage(code);
    await saveLanguage(code);
    setShowLanguageMenu(false);
  }, [i18n]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const raw = await AsyncStorage.getItem('harmonia_session');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) {
            router.replace('/(tabs)/actu');
            return;
          }
        }

        const savedAccounts = await getSavedAccounts();
        if (savedAccounts.length > 0) {
          router.replace('/saved-accounts');
          return;
        }
      } catch {
        // Aucune session valide et aucun compte mémorisé lisible,
        // on affiche la landing normalement
      }
      setSessionChecked(true);
    };
    checkSession();
  }, []);

  const fadeIn   = useRef(new Animated.Value(0)).current;
  const slideUp  = useRef(new Animated.Value(40)).current;
  const fadeIn2  = useRef(new Animated.Value(0)).current;
  const slideUp2 = useRef(new Animated.Value(30)).current;

  useEffect(() => {
    if (!sessionChecked) return;
    Animated.parallel([
      Animated.timing(fadeIn,  { toValue: 1, duration: 900, useNativeDriver: true }),
      Animated.timing(slideUp, { toValue: 0, duration: 900, useNativeDriver: true }),
    ]).start(() => {
      Animated.parallel([
        Animated.timing(fadeIn2,  { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.timing(slideUp2, { toValue: 0, duration: 600, useNativeDriver: true }),
      ]).start();
    });
  }, [sessionChecked]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    registerServiceWorker();
  }, []);

  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const installPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  const [isGenericFallback, setIsGenericFallback] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [showFirefoxHint, setShowFirefoxHint] = useState(false);
  const [showGenericHint, setShowGenericHint] = useState(false);

  const isRunningStandalone = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
    const mqStandalone = window.matchMedia?.('(display-mode: standalone)').matches;
    const iosStandalone = (window.navigator as any).standalone === true;
    return !!(mqStandalone || iosStandalone);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (isRunningStandalone()) {
      setShowInstallBtn(false);
      return;
    }

    const ua = window.navigator.userAgent || '';

    const noInstallSupport = /opera mini|ucbrowser|ucweb/i.test(ua);
    if (noInstallSupport) {
      setShowInstallBtn(false);
      return;
    }

    const iosDevice = /iphone|ipad|ipod/i.test(ua) && !(window as any).MSStream;
    if (iosDevice) {
      setIsIOS(true);
      setShowInstallBtn(true);
    }

    const firefoxBrowser = /firefox|fxios/i.test(ua);
    if (firefoxBrowser && !iosDevice) {
      setIsFirefox(true);
      setShowInstallBtn(true);
    }

    const onBeforeInstall = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPrompt(e);
      installPromptRef.current = e;
      setShowInstallBtn(true);
    };
    const onInstalled = () => {
      setShowInstallBtn(false);
      setInstallPrompt(null);
      installPromptRef.current = null;
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall as EventListener);
    window.addEventListener('appinstalled', onInstalled);

    const mq = window.matchMedia?.('(display-mode: standalone)');
    const onDisplayModeChange = (ev: MediaQueryListEvent) => {
      if (ev.matches) setShowInstallBtn(false);
    };
    mq?.addEventListener?.('change', onDisplayModeChange);

    const fallbackTimer = window.setTimeout(() => {
      if (!iosDevice && !firefoxBrowser && !installPromptRef.current) {
        setIsGenericFallback(true);
        setShowInstallBtn(true);
      }
    }, 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall as EventListener);
      window.removeEventListener('appinstalled', onInstalled);
      mq?.removeEventListener?.('change', onDisplayModeChange);
      window.clearTimeout(fallbackTimer);
    };
  }, [isRunningStandalone]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSHint(true);
      return;
    }
    if (isFirefox) {
      setShowFirefoxHint(true);
      return;
    }
    if (!installPrompt && isGenericFallback) {
      setShowGenericHint(true);
      return;
    }
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBtn(false);
      setInstallPrompt(null);
      installPromptRef.current = null;
    }
  };

  const go = useCallback(() => router.push('/login'), []);

  return (
    <View style={styles.root}>

      <Head>
        <title>Harmonia World — Révélez votre talent, créez votre boutique</title>
        <meta
          name="description"
          content="Harmonia World est la plateforme qui réunit concours de talents, communauté créative et marketplace : participez à des concours (arts, musique, performance, artisanat, stratégie), ou créez votre boutique en ligne avec votre propre site professionnel."
        />
        <meta property="og:title" content="Harmonia World — Révélez votre talent" />
        <meta
          property="og:description"
          content="Concours de talents, communauté créative et marketplace pour commerçants, artisans et entrepreneurs."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.harmoniaworld.world/" />
        <link rel="canonical" href="https://www.harmoniaworld.world/" />
      </Head>

      <StatusBar style="light" />

      {showLanguageMenu && (
        <TouchableOpacity
          style={styles.languageMenuBackdrop}
          activeOpacity={1}
          onPress={() => setShowLanguageMenu(false)}
        >
          <View style={styles.languageMenu}>
            {LANGUAGES.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                onPress={() => handleChangeLanguage(lang.code)}
                style={[
                  styles.languageMenuItem,
                  lang.code === currentLanguage.code && styles.languageMenuItemActive,
                ]}
              >
                <Text style={styles.languageMenuFlag}>{lang.flag}</Text>
                <Text style={styles.languageMenuLabel}>{lang.label}</Text>
                {lang.code === currentLanguage.code && (
                  <Ionicons name="checkmark" size={16} color={P.gold} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      )}

      {showIOSHint && (
        <View style={styles.iosHintOverlay}>
          <TouchableOpacity style={StyleSheet.absoluteFillObject} activeOpacity={1} onPress={() => setShowIOSHint(false)} />
          <View style={styles.iosHintCard}>
            <View style={styles.iosHintHeader}>
              <HarmoniaLogo size={28} showText={false} theme="light" />
              <Text style={styles.iosHintTitle}>{t('landing.installHints.ios.title')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="share-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.ios.step1')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="add-circle-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.ios.step2')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="checkmark-circle-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.ios.step3')}</Text>
            </View>
            <TouchableOpacity onPress={() => setShowIOSHint(false)} style={styles.iosHintClose}>
              <Text style={styles.iosHintCloseTxt}>{t('landing.installHints.ios.close')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {showFirefoxHint && (
        <View style={styles.iosHintOverlay}>
          <TouchableOpacity style={StyleSheet.absoluteFillObject} activeOpacity={1} onPress={() => setShowFirefoxHint(false)} />
          <View style={styles.iosHintCard}>
            <View style={styles.iosHintHeader}>
              <HarmoniaLogo size={28} showText={false} theme="light" />
              <Text style={styles.iosHintTitle}>{t('landing.installHints.firefox.title')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="menu-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.firefox.step1')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="download-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.firefox.step2')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="checkmark-circle-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.firefox.step3')}</Text>
            </View>
            <TouchableOpacity onPress={() => setShowFirefoxHint(false)} style={styles.iosHintClose}>
              <Text style={styles.iosHintCloseTxt}>{t('landing.installHints.firefox.close')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {showGenericHint && (
        <View style={styles.iosHintOverlay}>
          <TouchableOpacity style={StyleSheet.absoluteFillObject} activeOpacity={1} onPress={() => setShowGenericHint(false)} />
          <View style={styles.iosHintCard}>
            <View style={styles.iosHintHeader}>
              <HarmoniaLogo size={28} showText={false} theme="light" />
              <Text style={styles.iosHintTitle}>{t('landing.installHints.generic.title')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="ellipsis-vertical-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.generic.step1')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="download-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.generic.step2')}</Text>
            </View>
            <View style={styles.iosHintStep}>
              <Ionicons name="checkmark-circle-outline" size={18} color={P.gold} />
              <Text style={styles.iosHintTxt}>{t('landing.installHints.generic.step3')}</Text>
            </View>
            <TouchableOpacity onPress={() => setShowGenericHint(false)} style={styles.iosHintClose}>
              <Text style={styles.iosHintCloseTxt}>{t('landing.installHints.generic.close')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>

        <ImageBackground
          source={require('../assets/1.png')}
          style={[styles.hero, isWebLarge && { minHeight: '100vh' as any }]}
          imageStyle={{ opacity: 1, resizeMode: 'cover' }}
        >
          <LinearGradient
            colors={[
              'rgba(107,33,168,0.15)',
              'rgba(0,0,0,0.05)',
              'rgba(0,0,0,0.50)',
              'rgba(0,0,0,0.92)',
            ]}
            style={StyleSheet.absoluteFillObject}
            start={{ x: 0, y: 0 }}
            end={{ x: 0.3, y: 1 }}
          />

          <View style={[styles.navbar, isWebLarge && styles.navbarWeb]}>
            <View style={styles.navLogoRow}>
              <HarmoniaLogo size={34} showText={false} theme="light" />
              <Text style={styles.navBrand}>{t('landing.nav.brand')}</Text>
            </View>
            <View style={styles.navRightRow}>
              <TouchableOpacity
                onPress={() => setShowLanguageMenu((v) => !v)}
                style={styles.navLanguageBtn}
              >
                <Text style={styles.navLanguageFlag}>{currentLanguage.flag}</Text>
                <Text style={styles.navLanguageTxt}>{currentLanguage.label}</Text>
                <Ionicons name="chevron-down" size={12} color="#fff" />
              </TouchableOpacity>
              {showInstallBtn && (
                <TouchableOpacity onPress={handleInstall} style={styles.navInstallBtn}>
                  <Ionicons name="download-outline" size={14} color="#fff" />
                  <Text style={styles.navInstallTxt}>{t('landing.nav.install')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={go} style={styles.navCta}>
                <Text style={styles.navCtaTxt}>{t('landing.nav.cta')}</Text>
                <Ionicons name="arrow-forward" size={14} color={P.gold} />
              </TouchableOpacity>
            </View>
          </View>

          <View style={[styles.heroInner, isWebLarge && styles.heroInnerWeb]}>
            <Animated.View style={{ opacity: fadeIn, transform: [{ translateY: slideUp }] }}>
              <View style={styles.heroPill}>
                <Ionicons name="star" size={11} color={P.gold} />
                <Text style={styles.heroPillTxt}>{t('landing.hero.pill')}</Text>
                <Ionicons name="star" size={11} color={P.gold} />
              </View>

              <Text style={[styles.heroTitle, isWebLarge && { fontSize: 80, lineHeight: 84 }]}>
                {t('landing.hero.titleLine1')}{'\n'}{t('landing.hero.titleLine2')}{' '}
                <Text style={{ color: P.gold }}>{t('landing.hero.titleHighlight')}</Text>{'\n'}
                {t('landing.hero.titleLine3')}
              </Text>
            </Animated.View>

            <Animated.View style={{ opacity: fadeIn2, transform: [{ translateY: slideUp2 }] }}>
              <Text style={[styles.heroSub, isWebLarge && { fontSize: 19, maxWidth: 560 }]}>
                {t('landing.hero.sub')}
              </Text>

              <View style={styles.heroStatsRow}>
                <HeroStat value="🎯" label={t('landing.hero.statDisciplines')} />
                <View style={styles.heroStatDivider} />
                <HeroStat value="🏆" label={t('landing.hero.statConcours')} />
                <View style={styles.heroStatDivider} />
                <HeroStat value="✨" label={t('landing.hero.statTrophees')} />
              </View>

              <View style={styles.heroBadgesRow}>
                {[
                  { icon: 'trophy'  as const, label: t('landing.hero.badgeTrophees')   },
                  { icon: 'flash'   as const, label: t('landing.hero.badgeLive')        },
                  { icon: 'earth'   as const, label: t('landing.hero.badgeCommunaute') },
                ].map((b) => (
                  <View key={b.label} style={styles.heroBadge}>
                    <Ionicons name={b.icon} size={13} color={P.gold} />
                    <Text style={styles.heroBadgeTxt}>{b.label}</Text>
                  </View>
                ))}
              </View>

              <View style={[styles.heroActions, isWebLarge && { flexDirection: 'row', width: 'auto', gap: 16 }]}>
                <TouchableOpacity onPress={go} activeOpacity={0.88} style={isWebLarge ? { width: 280 } : { width: '100%' }}>
                  <LinearGradient colors={[P.gold, '#D97706']} style={styles.heroCta}
                    start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                    <Text style={styles.heroCtaTxt}>{t('landing.hero.ctaMain')}</Text>
                    <Ionicons name="arrow-forward-circle" size={22} color="#fff" />
                  </LinearGradient>
                </TouchableOpacity>
                {showInstallBtn && (
                  <TouchableOpacity onPress={handleInstall} activeOpacity={0.88}
                    style={[styles.heroInstallBtn, isWebLarge ? { width: 220 } : { width: '100%' }]}>
                    <Ionicons name="download-outline" size={18} color="#fff" />
                    <Text style={styles.heroInstallTxt}>{t('landing.hero.ctaInstall')}</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={go} style={styles.heroSecondary}>
                  <Text style={styles.heroSecondaryTxt}>{t('landing.hero.ctaSecondary')}</Text>
                  <Ionicons name="chevron-forward" size={16} color="rgba(255,255,255,0.85)" />
                </TouchableOpacity>
              </View>
            </Animated.View>
          </View>

          <View style={styles.scrollIndicator}>
            <Ionicons name="chevron-down" size={20} color="rgba(255,255,255,0.5)" />
          </View>
        </ImageBackground>

        <View style={styles.fullSection}>
          <View style={[styles.sectionPad, isWebLarge && styles.sectionPadWeb]}>
            <Chip label={t('landing.vision.chip')} color={P.purple} bg={P.purpleSoft} />
            <Text style={[styles.sectionTitle, isWebLarge && { fontSize: 52, lineHeight: 58 }]}>
              {t('landing.vision.titleLine1')}{'\n'}{t('landing.vision.titleLine2')} 🌍
            </Text>
            <Text style={[styles.sectionSub, isWebLarge && { maxWidth: 560 }]}>
              {t('landing.vision.sub')}
            </Text>
          </View>

          <Image source={require('../assets/2.png')}
            style={[styles.fullBleedImg, isWebLarge && { height: 560 }]} resizeMode="cover" />

          <View style={[styles.sectionPad, isWebLarge && styles.sectionPadWeb]}>
            <View style={[styles.valuesGrid, isWebLarge && { flexDirection: 'row' }]}>
              <ValueCard icon="🎨" title={t('landing.vision.card1Title')}
                desc={t('landing.vision.card1Desc')}
                color={P.purple} bg={P.purpleSoft} />
              <ValueCard icon="🤝" title={t('landing.vision.card2Title')}
                desc={t('landing.vision.card2Desc')}
                color={P.blue} bg={P.blueSoft} />
              <ValueCard icon="🚀" title={t('landing.vision.card3Title')}
                desc={t('landing.vision.card3Desc')}
                color={P.green} bg={P.greenSoft} />
            </View>
          </View>
        </View>

        <View style={styles.disciplinesSection}>
          <View style={styles.discBgOrb1} />
          <View style={styles.discBgOrb2} />

          <View style={[styles.sectionPad, isWebLarge && styles.sectionPadWeb]}>
            <Chip label={t('landing.disciplines.chip')} color="#A78BFA" bg="rgba(167,139,250,0.15)" />
            <Text style={[styles.sectionTitleLight, isWebLarge && { fontSize: 52, lineHeight: 58 }]}>
              {t('landing.disciplines.titleLine1')}{'\n'}{t('landing.disciplines.titleLine2')} 🏆
            </Text>
            <Text style={styles.sectionSubLight}>
              {t('landing.disciplines.sub')}
            </Text>

            <View style={styles.discStatsRow}>
              {[
                { value: '🎯', label: t('landing.disciplines.statDisciplines')  },
                { value: '🏆', label: t('landing.disciplines.statConcours')     },
                { value: '🔁', label: t('landing.disciplines.statHebdo') },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  <View style={styles.discStatItem}>
                    <Text style={styles.discStatValue}>{s.value}</Text>
                    <Text style={styles.discStatLabel}>{s.label}</Text>
                  </View>
                  {i < 2 && <View style={styles.discStatDivider} />}
                </React.Fragment>
              ))}
            </View>

            <View style={styles.trophyBanner}>
              <Ionicons name="trophy" size={16} color={P.gold} />
              <Text style={styles.trophyBannerTxt}>
                {t('landing.disciplines.trophyBanner')}
              </Text>
            </View>
          </View>

          <View style={[
            styles.discGrid,
            isWebLarge && styles.discGridWeb,
            isWebLarge && { paddingHorizontal: 64, maxWidth: 1200, alignSelf: 'center' as any, width: '100%' },
          ]}>
            {DISCIPLINES.map((disc) => (
              <DisciplineCard
                key={disc.id}
                discipline={disc}
                onPress={go}
                isWebLarge={isWebLarge}
                ctaLabel={t('landing.disciplines.ctaTrophies')}
              />
            ))}
          </View>

          <View style={styles.discHintRow}>
            <Ionicons name="hand-left-outline" size={12} color="rgba(167,139,250,0.5)" />
            <Text style={styles.discHintTxt}>{t('landing.disciplines.hint')}</Text>
          </View>
        </View>

        {/* --- NOUVELLE SECTION: SOLUTION PRO --- */}
        <View style={styles.marketSection}>
          <View style={styles.marketBgOrb1} />
          <View style={styles.marketBgOrb2} />

          <View style={[styles.sectionPad, isWebLarge && styles.sectionPadWeb]}>
            <Chip label={t('landing.market.chip')} color="#F59E0B" bg="rgba(245,158,11,0.15)" />
            <Text style={[styles.sectionTitleLight, isWebLarge && { fontSize: 52, lineHeight: 58 }]}>
              {t('landing.market.titleLine1')}{'\n'}{t('landing.market.titleLine2')} 🛍️
            </Text>
            <Text style={styles.sectionSubLight}>
              {t('landing.market.sub')}
            </Text>

            <View style={styles.discStatsRow}>
              {[
                { value: '🏪', label: t('landing.market.statBoutiques') },
                { value: '📦', label: t('landing.market.statProduits') },
                { value: '🌍', label: t('landing.market.statMonde') },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  <View style={styles.discStatItem}>
                    <Text style={styles.discStatValue}>{s.value}</Text>
                    <Text style={styles.discStatLabel}>{s.label}</Text>
                  </View>
                  {i < 2 && <View style={styles.discStatDivider} />}
                </React.Fragment>
              ))}
            </View>
          </View>

          <View style={[
            styles.marketGrid,
            isWebLarge && styles.marketGridWeb,
            isWebLarge && { paddingHorizontal: 64, maxWidth: 1200, alignSelf: 'center' as any, width: '100%' },
          ]}>
            {[
              { icon: 'globe-outline' as const, titleKey: 'feature1Title', descKey: 'feature1Desc' },
              { icon: 'link-outline' as const, titleKey: 'feature2Title', descKey: 'feature2Desc' },
              { icon: 'pricetag-outline' as const, titleKey: 'feature3Title', descKey: 'feature3Desc' },
              { icon: 'share-social-outline' as const, titleKey: 'feature4Title', descKey: 'feature4Desc' },
            ].map((f, i) => (
              <View key={i} style={isWebLarge ? styles.marketCardWrapWeb : styles.marketCardWrapMobile}>
                <View style={styles.marketCard}>
                  <View style={styles.marketCardIconWrap}>
                    <Ionicons name={f.icon} size={22} color="#F59E0B" />
                  </View>
                  <Text style={styles.marketCardTitle}>{t(`landing.market.${f.titleKey}`)}</Text>
                  <Text style={styles.marketCardDesc}>{t(`landing.market.${f.descKey}`)}</Text>
                </View>
              </View>
            ))}
          </View>

          <View style={[styles.sectionPad, isWebLarge && styles.sectionPadWeb, { paddingTop: 12, alignItems: isWebLarge ? 'center' : 'flex-start' }]}>
            <TouchableOpacity onPress={go} activeOpacity={0.88} style={isWebLarge ? { width: 300 } : { width: '100%' }}>
              <LinearGradient colors={[P.gold, '#D97706']} style={styles.sectionCta}
                start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                <Text style={styles.sectionCtaTxt}>{t('landing.market.cta')}</Text>
                <Ionicons name="storefront" size={18} color="#fff" />
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </View>
        {/* --- FIN NOUVELLE SECTION --- */}

        <View style={styles.challengeSection}>
          {isWebLarge ? (
            <View style={styles.challengeSplit}>
              <Image source={require('../assets/3.png')} style={styles.challengeImgWeb} resizeMode="cover" />
              <View style={styles.challengeContent}>
                <Chip label={t('landing.challenge.chip')} color={P.purple} bg={P.purpleSoft} />
                <Text style={[styles.sectionTitle, { fontSize: 44, lineHeight: 50, marginTop: 20 }]}>
                  {t('landing.challenge.title')} 🏆
                </Text>
                <Text style={styles.sectionSub}>
                  {t('landing.challenge.sub')}
                </Text>

                <View style={styles.challengeMiniList}>
                  {DISCIPLINES.map((d) => (
                    <View key={d.id} style={styles.challengeMiniItem}>
                      <Ionicons name={d.icon} size={14} color={d.accent} />
                      <Text style={styles.challengeMiniLabel}>{d.label}</Text>
                    </View>
                  ))}
                </View>

                <TouchableOpacity onPress={go} activeOpacity={0.88} style={{ alignSelf: 'flex-start', marginTop: 8 }}>
                  <LinearGradient colors={[P.purple, P.purpleL]} style={styles.sectionCta}
                    start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                    <Text style={styles.sectionCtaTxt}>{t('landing.challenge.cta')}</Text>
                    <Ionicons name="sparkles" size={18} color="#fff" />
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <>
              <Image source={require('../assets/3.png')} style={styles.fullBleedImg} resizeMode="cover" />
              <View style={styles.challengeContentMobile}>
                <Chip label={t('landing.challenge.chip')} color={P.purple} bg={P.purpleSoft} />
                <Text style={[styles.sectionTitle, { marginTop: 20 }]}>
                  {t('landing.challenge.title')} 🏆
                </Text>
                <Text style={styles.sectionSub}>
                  {t('landing.challenge.subMobile')}
                </Text>

                <View style={styles.challengeMiniListMobile}>
                  {DISCIPLINES.map((d) => (
                    <View key={d.id} style={[styles.challengeMiniPill, { borderColor: d.accent + '50', backgroundColor: d.accent + '12' }]}>
                      <Ionicons name={d.icon} size={12} color={d.accent} />
                      <Text style={[styles.challengeMiniPillTxt, { color: d.accent }]}>{d.label}</Text>
                    </View>
                  ))}
                </View>

                <TouchableOpacity onPress={go} activeOpacity={0.88}>
                  <LinearGradient colors={[P.purple, P.purpleL]} style={styles.sectionCta}
                    start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                    <Text style={styles.sectionCtaTxt}>{t('landing.challenge.cta')}</Text>
                    <Ionicons name="sparkles" size={18} color="#fff" />
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>

        <View style={styles.fullSection}>
          <View style={[styles.sectionPad, isWebLarge && styles.sectionPadWeb,
            { alignItems: isWebLarge ? 'center' : 'flex-start' }]}>
            <Chip label={t('landing.community.chip')} color={P.green} bg={P.greenSoft} />
            <Text style={[styles.sectionTitle,
              isWebLarge && { textAlign: 'center', fontSize: 52, lineHeight: 58 }]}>
              {t('landing.community.titleLine1')}{'\n'}{t('landing.community.titleLine2')} 💎
            </Text>
            <Text style={[styles.sectionSub, isWebLarge && { textAlign: 'center', maxWidth: 560 }]}>
              {t('landing.community.sub')}
            </Text>
          </View>

          <View style={styles.communityImgWrapper}>
            <Image source={require('../assets/4.png')}
              style={[styles.fullBleedImg, isWebLarge && { height: 600 }, { marginBottom: 0 }]}
              resizeMode="cover" />
            <LinearGradient colors={['transparent', 'rgba(0,0,0,0.72)']} style={styles.communityOverlay}
              start={{ x: 0, y: 0.3 }} end={{ x: 0, y: 1 }}>
              <TouchableOpacity onPress={go} activeOpacity={0.88}
                style={[styles.communityCtaWrap, isWebLarge && { width: 340 }]}>
                <LinearGradient colors={[P.gold, '#D97706']} style={styles.sectionCta}
                  start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                  <Text style={styles.sectionCtaTxt}>{t('landing.community.cta')}</Text>
                  <Ionicons name="people" size={18} color="#fff" />
                </LinearGradient>
              </TouchableOpacity>
            </LinearGradient>
          </View>
        </View>

        <LinearGradient colors={[P.purple, '#3B0764']} style={styles.finalSection}
          start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}>
          <View style={styles.finalOrb1} />
          <View style={styles.finalOrb2} />
          <View style={styles.finalOrb3} />

          <View style={[styles.finalInner, isWebLarge && { maxWidth: 760, alignSelf: 'center' }]}>
            <View style={styles.finalLogoRow}>
              <HarmoniaLogo size={48} showText={false} theme="light" />
              <Text style={styles.finalLogoName}>{t('landing.nav.brand')}</Text>
            </View>

            <View style={styles.finalDivider}>
              <View style={styles.finalDividerLine} />
              <View style={styles.finalDividerDot} />
              <View style={styles.finalDividerLine} />
            </View>

            <Text style={[styles.finalTitle, isWebLarge && { fontSize: 64, lineHeight: 70 }]}>
              {t('landing.final.titleLine1')}{'\n'}{t('landing.final.titleLine2')} ✨
            </Text>
            <Text style={styles.finalSub}>
              {t('landing.final.sub')}
            </Text>

            <TouchableOpacity onPress={go} activeOpacity={0.88} style={{ width: isWebLarge ? 400 : '100%' }}>
              <LinearGradient colors={[P.gold, '#D97706']} style={styles.finalCta}
                start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                <Text style={styles.finalCtaTxt}>🌟 {t('landing.final.cta')}</Text>
              </LinearGradient>
            </TouchableOpacity>

            <Text style={styles.finalNote}>{t('landing.final.note')}</Text>

            <View style={styles.finalBadges}>
              {DISCIPLINES.map((d) => (
                <View key={d.id} style={[styles.finalTag, { borderColor: d.accent + '40' }]}>
                  <Ionicons name={d.icon} size={11} color={d.accent} />
                  <Text style={[styles.finalTagTxt, { color: d.accent + 'DD' }]}>{d.label}</Text>
                </View>
              ))}
            </View>
          </View>
        </LinearGradient>

        <View style={[styles.footer, isWebLarge && styles.footerWeb]}>
          <View style={styles.footerLogoRow}>
            <HarmoniaLogo size={32} showText={false} theme="dark" />
            <Text style={styles.footerBrand}>{t('landing.nav.brand')}</Text>
          </View>
          <Text style={styles.footerTagline}>{t('landing.footer.tagline')}</Text>
          <View style={styles.footerSep} />
          <Text style={styles.footerCopy}>© {new Date().getFullYear()} {t('landing.nav.brand')} · {t('landing.footer.copyright')}</Text>
        </View>

      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root:          { flex: 1, backgroundColor: P.bg },
  scrollContent: { paddingBottom: 0 },

  navRightRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },

  navLanguageBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.20)',
    borderRadius: 20, paddingHorizontal: 12, paddingVertical: 9,
  },
  navLanguageFlag: { fontSize: 14 },
  navLanguageTxt:  { color: '#fff', fontSize: 12, fontWeight: '700' },

  languageMenuBackdrop: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 3000,
  },
  languageMenu: {
    position: 'absolute', top: 100, right: 24,
    backgroundColor: '#1A0840', borderRadius: 16,
    paddingVertical: 6, minWidth: 140,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    shadowColor: 'rgba(0,0,0,0.5)',
    shadowOffset: { width: 0, height: 8 }, shadowOpacity: 1, shadowRadius: 20,
    elevation: 12,
  },
  languageMenuItem: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 16, paddingVertical: 12,
  },
  languageMenuItemActive: { backgroundColor: 'rgba(245,158,11,0.12)' },
  languageMenuFlag:  { fontSize: 16 },
  languageMenuLabel: { color: '#fff', fontSize: 14, fontWeight: '600', flex: 1 },

  navInstallBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: 'rgba(245,158,11,0.22)',
    borderWidth: 1, borderColor: 'rgba(245,158,11,0.45)',
    borderRadius: 24, paddingHorizontal: 14, paddingVertical: 10,
  },
  navInstallTxt: { color: '#fff', fontSize: 13, fontWeight: '700' },

  heroInstallBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 19, borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.35)',
  },
  heroInstallTxt: { color: '#fff', fontSize: 15, fontWeight: '800' },

  iosHintOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end', zIndex: 2000,
  },
  iosHintCard: {
    backgroundColor: '#1A0840', borderTopLeftRadius: 24, borderTopRightRadius: 24,
    padding: 24, paddingBottom: 36, gap: 14,
  },
  iosHintHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6 },
  iosHintTitle:  { fontSize: 18, fontWeight: '900', color: '#fff' },
  iosHintStep:   { flexDirection: 'row', alignItems: 'center', gap: 10 },
  iosHintTxt:    { color: 'rgba(255,255,255,0.85)', fontSize: 14, flex: 1 },
  iosHintClose: {
    marginTop: 10, backgroundColor: P.gold, borderRadius: 14,
    paddingVertical: 14, alignItems: 'center',
  },
  iosHintCloseTxt: { color: '#1A0840', fontWeight: '800', fontSize: 15 },

  navbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 24, paddingTop: 56,
    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
  },
  navbarWeb:  { paddingHorizontal: 48, maxWidth: 1200, alignSelf: 'center', width: '100%' },
  navLogoRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  navBrand:   { fontSize: 22, fontWeight: '900', color: '#fff', letterSpacing: -0.3 },
  navCta: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.20)',
    borderRadius: 24, paddingHorizontal: 18, paddingVertical: 10,
  },
  navCtaTxt: { color: '#fff', fontSize: 14, fontWeight: '700' },

  hero:         { width: '100%', minHeight: '90vh' as any, justifyContent: 'flex-end' },
  heroInner:    { paddingHorizontal: 24, paddingBottom: 60, paddingTop: 140 },
  heroInnerWeb: { paddingHorizontal: 64, maxWidth: 1200, alignSelf: 'center', width: '100%', paddingTop: 160 },

  heroPill: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    backgroundColor: 'rgba(0,0,0,0.28)',
    borderWidth: 1, borderColor: 'rgba(255,215,0,0.32)',
    paddingHorizontal: 14, paddingVertical: 6,
    borderRadius: 100, alignSelf: 'flex-start', marginBottom: 22,
  },
  heroPillTxt: { color: P.gold, fontSize: 10, fontWeight: '800', letterSpacing: 1.5, textTransform: 'uppercase' },

  heroTitle: {
    fontSize: 52, fontWeight: '900', color: '#fff',
    lineHeight: 58, marginBottom: 20, letterSpacing: -1,
  },
  heroSub: {
    fontSize: 17, color: 'rgba(255,255,255,0.80)',
    lineHeight: 28, marginBottom: 20,
  },

  heroStatsRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.32)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 16, paddingVertical: 14, paddingHorizontal: 20,
    marginBottom: 20, alignSelf: 'flex-start',
  },
  heroStat:       { alignItems: 'center', paddingHorizontal: 16 },
  heroStatValue:  { fontSize: 22, fontWeight: '900', color: '#fff', letterSpacing: -0.5 },
  heroStatLabel:  { fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: '600', marginTop: 2, letterSpacing: 0.5 },
  heroStatDivider:{ width: 1, height: 30, backgroundColor: 'rgba(255,255,255,0.15)' },

  heroBadgesRow: { flexDirection: 'row', gap: 8, marginBottom: 28 },
  heroBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    backgroundColor: 'rgba(0,0,0,0.30)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.13)',
    paddingHorizontal: 11, paddingVertical: 6, borderRadius: 10,
  },
  heroBadgeTxt: { color: '#EDE9FE', fontSize: 11, fontWeight: '600' },

  heroActions:      { gap: 14, alignItems: 'center' },
  heroCta: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 10, paddingVertical: 19, borderRadius: 18,
  },
  heroCtaTxt:       { color: '#fff', fontSize: 16, fontWeight: '800', letterSpacing: 0.2 },
  heroSecondary:    { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'center', paddingVertical: 10 },
  heroSecondaryTxt: { color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: '700' },

  scrollIndicator: { position: 'absolute', bottom: 20, left: 0, right: 0, alignItems: 'center' },

  fullSection:   { width: '100%' },
  sectionPad:    { paddingHorizontal: 24, paddingTop: 60, paddingBottom: 36 },
  sectionPadWeb: { paddingHorizontal: 64, maxWidth: 1200, alignSelf: 'center', width: '100%' },

  sectionTitle: {
    fontSize: 38, fontWeight: '900', color: P.text,
    lineHeight: 44, marginTop: 18, marginBottom: 16, letterSpacing: -0.7,
  },
  sectionSub:    { fontSize: 16, color: P.textSub, lineHeight: 27, marginBottom: 32 },
  fullBleedImg:  { width: '100%', height: 300 },

  valuesGrid: { flexDirection: 'column', gap: 16 },
  valueCard: {
    backgroundColor: P.white, padding: 28, borderRadius: 20,
    shadowColor: 'rgba(0,0,0,0.07)',
    shadowOffset: { width: 0, height: 6 }, shadowOpacity: 1, shadowRadius: 20,
    elevation: 4, flex: 1,
  },
  valueIconBox:  { width: 58, height: 58, borderRadius: 20, justifyContent: 'center', alignItems: 'center', marginBottom: 18 },
  valueAccent:   { width: 28, height: 3, borderRadius: 2, marginBottom: 14 },
  valueTitle:    { fontSize: 18, fontWeight: '800', color: P.text, marginBottom: 8 },
  valueDesc:     { fontSize: 14, color: P.textSub, lineHeight: 22 },

  disciplinesSection: {
    width: '100%', backgroundColor: '#0E0017',
    overflow: 'hidden', paddingBottom: 48,
  },
  discBgOrb1: {
    position: 'absolute', width: 400, height: 400, borderRadius: 200,
    backgroundColor: 'rgba(138,43,226,0.12)', top: -100, right: -80,
  },
  discBgOrb2: {
    position: 'absolute', width: 280, height: 280, borderRadius: 140,
    backgroundColor: 'rgba(245,158,11,0.06)', bottom: -60, left: -60,
  },

  sectionTitleLight: {
    fontSize: 38, fontWeight: '900', color: '#F5F0FF',
    lineHeight: 44, marginTop: 18, marginBottom: 16, letterSpacing: -0.7,
  },
  sectionSubLight: { fontSize: 16, color: '#9B82C4', lineHeight: 27, marginBottom: 24 },

  discStatsRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 14, paddingVertical: 14, paddingHorizontal: 16,
    alignSelf: 'flex-start', marginBottom: 20,
  },
  discStatItem:    { alignItems: 'center', paddingHorizontal: 14 },
  discStatValue:   { fontSize: 20, fontWeight: '900', color: '#F5F0FF', letterSpacing: -0.3 },
  discStatLabel:   { fontSize: 10, color: '#9B82C4', fontWeight: '600', marginTop: 2, letterSpacing: 0.6 },
  discStatDivider: { width: 1, height: 26, backgroundColor: 'rgba(255,255,255,0.10)' },

  trophyBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: 'rgba(255,215,0,0.08)',
    borderWidth: 1, borderColor: 'rgba(255,215,0,0.25)',
    borderRadius: 14, paddingHorizontal: 16, paddingVertical: 10,
    alignSelf: 'flex-start', marginBottom: 8,
  },
  trophyBannerTxt: { fontSize: 12, color: '#FFD700', fontWeight: '600', fontStyle: 'italic', flex: 1 },

  discGrid:    { paddingHorizontal: 16, gap: 14 },
  discGridWeb: { flexDirection: 'row', flexWrap: 'wrap' },

  discCardWrapMobile: { width: '100%' },
  discCardWrapWeb:    { width: '48%' },

  discCard: {
    borderRadius: 22, overflow: 'hidden',
    shadowColor: 'rgba(0,0,0,0.5)',
    shadowOffset: { width: 0, height: 8 }, shadowOpacity: 1, shadowRadius: 18,
    elevation: 8,
  },
  discCardInner: {
    padding: 20, overflow: 'hidden',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)',
    gap: 12,
  },
  discOrb: {
    position: 'absolute', width: 140, height: 140, borderRadius: 70,
    top: -40, right: -40, opacity: 0.12,
  },

  discHeader:  { flexDirection: 'row', alignItems: 'center', gap: 12 },
  discIconWrap: {
    width: 44, height: 44, borderRadius: 14,
    borderWidth: 1, backgroundColor: 'rgba(255,255,255,0.08)',
    justifyContent: 'center', alignItems: 'center',
  },
  discLabel:   { fontSize: 15, fontWeight: '900', color: '#FFFFFF', letterSpacing: -0.2, marginBottom: 2 },
  discTagline: { fontSize: 11, fontWeight: '600', letterSpacing: 0.1 },

  discDesc: {
    fontSize: 12.5, color: 'rgba(255,255,255,0.60)',
    lineHeight: 19, letterSpacing: 0.1,
  },

  discLine: { height: 1, borderRadius: 1 },

  discItemsCol: { gap: 8 },
  discItemRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
  },
  discItemDot:  { width: 6, height: 6, borderRadius: 3 },
  discItemName: { fontSize: 13, fontWeight: '700', color: '#FFFFFF', marginBottom: 1 },
  discItemSub:  { fontSize: 10, fontWeight: '500', letterSpacing: 0.1 },

  discCta: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    borderWidth: 1, borderRadius: 10,
    paddingHorizontal: 12, paddingVertical: 7,
    backgroundColor: 'rgba(255,255,255,0.03)',
    alignSelf: 'flex-start',
  },
  discCtaTxt: { fontSize: 10, fontWeight: '700', letterSpacing: 0.3 },

  discHintRow: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    justifyContent: 'center', marginTop: 20,
  },
  discHintTxt: { fontSize: 11, color: 'rgba(167,139,250,0.5)', fontStyle: 'italic' },

  // --- STYLES NOUVELLE SECTION SOLUTION PRO ---
  marketSection: {
    width: '100%', backgroundColor: '#0E0017',
    overflow: 'hidden', paddingBottom: 48, paddingTop: 8,
  },
  marketBgOrb1: {
    position: 'absolute', width: 360, height: 360, borderRadius: 180,
    backgroundColor: 'rgba(245,158,11,0.10)', top: -80, left: -100,
  },
  marketBgOrb2: {
    position: 'absolute', width: 260, height: 260, borderRadius: 130,
    backgroundColor: 'rgba(124,58,237,0.10)', bottom: -60, right: -60,
  },

  marketGrid:    { paddingHorizontal: 16, gap: 14, marginTop: 12 },
  marketGridWeb: { flexDirection: 'row', flexWrap: 'wrap' },

  marketCardWrapMobile: { width: '100%' },
  marketCardWrapWeb:    { width: '48%' },

  marketCard: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 18, padding: 22,
  },
  marketCardIconWrap: {
    width: 44, height: 44, borderRadius: 14,
    borderWidth: 1, borderColor: 'rgba(245,158,11,0.35)',
    backgroundColor: 'rgba(245,158,11,0.10)',
    justifyContent: 'center', alignItems: 'center', marginBottom: 14,
  },
  marketCardTitle: { fontSize: 15, fontWeight: '800', color: '#F5F0FF', marginBottom: 6, letterSpacing: -0.2 },
  marketCardDesc:  { fontSize: 12.5, color: '#9B82C4', lineHeight: 19 },
  // ---------------------------------------------

  challengeSection:       { width: '100%', backgroundColor: P.bg },
  challengeSplit:         { flexDirection: 'row', minHeight: 560 },
  challengeImgWeb:        { flex: 1, height: '100%' as any },
  challengeContent:       { flex: 1, paddingHorizontal: 56, paddingVertical: 72, justifyContent: 'center' },
  challengeContentMobile: { paddingHorizontal: 24, paddingTop: 36, paddingBottom: 60 },

  challengeMiniList:   { gap: 8, marginBottom: 28 },
  challengeMiniItem:   {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 6,
  },
  challengeMiniLabel:  { flex: 1, fontSize: 13, fontWeight: '600', color: P.text },

  challengeMiniListMobile: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 24 },
  challengeMiniPill: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    borderWidth: 1, borderRadius: 20,
    paddingHorizontal: 10, paddingVertical: 5,
  },
  challengeMiniPillTxt: { fontSize: 11, fontWeight: '700' },

  sectionCta: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 10, paddingVertical: 17, paddingHorizontal: 28, borderRadius: 16,
  },
  sectionCtaTxt: { color: '#fff', fontSize: 15, fontWeight: '800' },

  communityImgWrapper: { position: 'relative', width: '100%' },
  communityOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0, height: 180,
    justifyContent: 'flex-end', paddingBottom: 36, alignItems: 'center',
  },
  communityCtaWrap: { width: '85%' },

  chip:    { alignSelf: 'flex-start', borderRadius: 24, paddingHorizontal: 16, paddingVertical: 7 },
  chipTxt: { fontSize: 11, fontWeight: '800', letterSpacing: 1.2 },

  finalSection: { width: '100%', paddingVertical: 90, paddingHorizontal: 28, overflow: 'hidden' },
  finalOrb1: {
    position: 'absolute', width: 500, height: 500, borderRadius: 250,
    backgroundColor: 'rgba(255,255,255,0.04)', top: -180, right: -140,
  },
  finalOrb2: {
    position: 'absolute', width: 300, height: 300, borderRadius: 150,
    backgroundColor: 'rgba(245,158,11,0.07)', bottom: -100, left: -80,
  },
  finalOrb3: {
    position: 'absolute', width: 200, height: 200, borderRadius: 100,
    backgroundColor: 'rgba(124,58,237,0.15)', top: '40%', left: '40%',
  },
  finalInner:       { alignItems: 'center', width: '100%' },
  finalLogoRow:     { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 28 },
  finalLogoName:    { fontSize: 30, fontWeight: '900', color: '#fff', letterSpacing: -0.3 },
  finalDivider:     { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 32, width: 160 },
  finalDividerLine: { flex: 1, height: 1, backgroundColor: 'rgba(255,255,255,0.20)' },
  finalDividerDot:  { width: 6, height: 6, borderRadius: 3, backgroundColor: P.gold },
  finalTitle: {
    fontSize: 44, fontWeight: '900', color: '#fff',
    textAlign: 'center', lineHeight: 52, marginBottom: 20, letterSpacing: -0.8,
  },
  finalSub: {
    fontSize: 16, color: 'rgba(255,255,255,0.75)',
    textAlign: 'center', lineHeight: 28, marginBottom: 40,
  },
  finalCta: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: 21, borderRadius: 20, width: '100%',
  },
  finalCtaTxt:  { color: '#fff', fontSize: 17, fontWeight: '800' },
  finalNote:    { color: 'rgba(255,255,255,0.45)', fontSize: 13, marginTop: 18, marginBottom: 36, letterSpacing: 0.3 },
  finalBadges:  { flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center' },
  finalTag: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderRadius: 24,
    paddingHorizontal: 14, paddingVertical: 8,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  finalTagTxt: { fontSize: 12, fontWeight: '700' },

  footer: {
    backgroundColor: P.white, paddingHorizontal: 28, paddingVertical: 52,
    alignItems: 'center', borderTopWidth: 1, borderTopColor: P.border,
  },
  footerWeb:     { paddingHorizontal: 64 },
  footerLogoRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 },
  footerBrand:   { fontSize: 22, fontWeight: '900', color: P.text, letterSpacing: -0.3 },
  footerTagline: { fontSize: 14, color: P.textSub, fontStyle: 'italic', marginBottom: 28, letterSpacing: 0.2 },
  footerSep:     { width: 60, height: 1.5, backgroundColor: P.border, marginBottom: 20, borderRadius: 1 },
  footerCopy:    { fontSize: 12, color: P.textLight, fontWeight: '500', letterSpacing: 0.3 },
});
